import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import LazyImage from 'components/UI/LazyImage';
import Icon from 'components/UI/Icon';
import VideoCaption from './VideoCaption';
import { useInView } from 'react-intersection-observer';

import * as styles from './MediaPlayer.module.scss';

function MediaPlayer({
  videoPoster,
  videoSrc,
  videoCaption,
  preload = 'none',
  previewStartTime,
  previewDuration = 4000,
  type = 'video/mp4',
  onClick,
  trackProps,
  theme,
  className,
  setGlobalPlayback,
  autoPlayVideo,
}) {
  const [showedPoster, setShowedPoster] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [playback, setPlayback] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const [videoPromise, setVideoPromise] = useState(null);
  const [previewPromise, setPreviewPromise] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const video = useRef(null);
  const [inViewRef, inView] = useInView();

  useEffect(() => {
    if (!inView) {
      pauseVideo(videoPromise);
    }
  }, [inView]);

  useEffect(() => {
    if (autoPlayVideo) {
      onTogglePlayback();
    }
  }, [autoPlayVideo]);

  useEffect(() => {
    if (showedPoster) {
      video.current.onplay = () => {
        setVideoPlayback(true);
      };
      video.current.onpause = () => {
        setVideoPlayback(false);
      };
    }
  }, [showedPoster]);

  const setVideoPlayback = status => {
    if (setGlobalPlayback) {
      setGlobalPlayback(status);
    }

    setPlayback(status);
  };

  const stopPreview = () => {
    if (!previewStartTime || showedPoster) return;

    if (previewPromise) {
      previewPromise.then(() => {
        video.current.pause();
        video.current.currentTime = 0;
        video.current.muted = false;
      });
    }
    setPreviewPromise(null);
    clearTimeout(timeoutId);
    setTimeoutId(null);
    setShowPreview(false);
  };

  const startPreview = () => {
    if (
      window.innerWidth < 1024 ||
      !previewStartTime ||
      showedPoster ||
      (preload !== 'none' && !videoDuration)
    ) {
      return;
    }

    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    video.current.muted = true;
    video.current.currentTime = previewStartTime;

    const promise = video.current.play();
    setPreviewPromise(promise);

    if (promise) {
      promise.then(() => {
        video.current.play();
        setShowPreview(true);
      });
    }

    if (previewDuration) {
      const duration = videoDuration * 1000;
      const preparedPreviewDuration = previewDuration > duration ? duration : previewDuration;
      setTimeoutId(setTimeout(stopPreview, preparedPreviewDuration));
    } else {
      video.current.loop = true;
    }
  };

  const playVideo = playPromise => {
    if (playPromise) {
      video.current.volume = 0.1;
      video.current.controls = true;
      video.current.controlsList = 'nodownload';
      playPromise.then(() => {
        video.current.play();
        setVideoPlayback(true);
      });
      setShowedPoster(true);
    }
  };

  const pauseVideo = playPromise => {
    if (playPromise) {
      playPromise.then(() => {
        video.current.pause();
        setVideoPlayback(false);
      });
    }
  };

  const onTogglePlayback = () => {
    const playPromise = video.current.play();
    setVideoPromise(playPromise);

    if (showPreview) {
      stopPreview();
    }

    if (playPromise) {
      if (playback) {
        pauseVideo(playPromise);
      } else {
        playVideo(playPromise);
      }
    }
  };

  const handleLoadedMetadata = () => {
    const currentVideo = video.current;

    if (!currentVideo) return;

    setVideoDuration(currentVideo.duration);
  };

  const onImageLoad = () => {
    if (preload !== 'none') {
      video.current.preload = preload;
    }
  };

  return (
    <div className={cn(styles.mediaPlayer, theme && styles[theme], className)}>
      <div
        className={styles.videoWrapper}
        ref={inViewRef}
        onClick={onClick ?? onTogglePlayback}
        onMouseEnter={startPreview}
        onMouseLeave={stopPreview}
      >
        {!showedPoster && <Icon name="video-play-circle" className={styles.buttonPlay} />}
        {videoPoster && !showedPoster && (
          <LazyImage
            src={videoPoster}
            className={cn(styles.poster, showPreview && styles.hidden)}
            alt="poster"
            onLoad={onImageLoad}
          />
        )}
        <video
          preload="none"
          className={styles.video}
          playsInline={true}
          ref={video}
          onLoadedMetadata={handleLoadedMetadata}
        >
          <track kind="captions" {...trackProps} />
          <source src={videoSrc} type={type} />
        </video>
      </div>
      {videoCaption && !showedPoster && !showPreview && (
        <VideoCaption videoDuration={timeFormatting(videoDuration)} {...videoCaption} />
      )}
    </div>
  );
}

function timeFormatting(seconds) {
  const minutes = Math.floor(seconds / 60);
  const residualSeconds = Math.floor(seconds - minutes * 60);

  if (!seconds) return;

  return `${minutes}:${String(residualSeconds < 10 ? '0' : '')}${residualSeconds}`;
}

export default React.memo(MediaPlayer);
